import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useTranslation } from "react-i18next";
import AuthModal from "./AuthModal";
import ChangePasswordModal from "./ChangePasswordModal";

export default function UserMenu() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { user, isAuthenticated, isLoading, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [isDropdownOpen]);

  // Close dropdown on escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener("keydown", handleEscape);
      return () => {
        document.removeEventListener("keydown", handleEscape);
      };
    }
  }, [isDropdownOpen]);

  const handleLogout = async () => {
    setIsDropdownOpen(false);
    await logout();
  };

  const handleProfileClick = () => {
    setIsDropdownOpen(false);
    navigate("/profile");
  };

  const handleChangePasswordClick = () => {
    setIsDropdownOpen(false);
    setIsChangePasswordOpen(true);
  };

  if (isLoading) {
    return (
      <div className="w-8 h-8 rounded-full bg-hover animate-pulse" />
    );
  }

  if (!isAuthenticated) {
    return (
      <>
        <button
          onClick={() => setIsAuthModalOpen(true)}
          className="px-4 py-2 bg-primary text-white rounded-md
            hover:bg-primary-hover transition-colors font-medium
            text-sm md:text-base"
        >
          {t('common.login', 'Login')}
        </button>
        <AuthModal
          isOpen={isAuthModalOpen}
          onClose={() => setIsAuthModalOpen(false)}
        />
      </>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      {/* User button with icon */}
      <button
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        className="flex items-center gap-2 p-2 rounded-md
          hover:bg-hover transition-colors"
        aria-label="User menu"
        aria-expanded={isDropdownOpen}
        aria-haspopup="true"
      >
        {/* User icon */}
        <div
          className="w-8 h-8 rounded-full bg-primary 
          flex items-center justify-center text-white font-medium"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 
                7h14a7 7 0 00-7-7z"
            />
          </svg>
        </div>

        {/* Username (hidden on mobile) */}
        <span className="hidden md:inline text-primary-content">
          {user.username}
        </span>

        {/* Dropdown arrow */}
        <svg
          className={`w-4 h-4 text-secondary-content transition-transform
            ${isDropdownOpen ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {/* Dropdown menu */}
      {isDropdownOpen && (
        <div
          className="absolute right-0 mt-2 w-64 bg-surface 
          rounded-lg shadow-lg border border-divider py-2 z-50"
          role="menu"
        >
          {/* User info */}
          <div className="px-4 py-3 border-b border-divider">
            <p className="text-sm font-medium text-primary-content">
              {user.username}
            </p>
            <p className="text-xs text-secondary-content mt-1">
              {user.email}
            </p>
            {user.is_admin && (
              <span
                className="inline-block mt-2 px-2 py-1 
                bg-primary bg-opacity-10 text-primary text-xs 
                rounded-md font-medium"
              >
                {t('profile.roleStaff', 'Staff')}
              </span>
            )}
          </div>

          {/* Pilot badge (if claimed) */}
          {user.pilot && (
            <div className="px-4 py-3 border-b border-divider">
              <div className="flex items-center gap-2">
                <svg
                  className="w-4 h-4 text-primary"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 
                    3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 
                    1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 
                    1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 
                    2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 
                    00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 
                    1 0 00.951-.69l1.07-3.292z"
                  />
                </svg>
                <div>
                  <p className="text-xs text-secondary-content">
                    {t('profile.pilotProfile', 'Pilot Profile')}
                  </p>
                  <p className="text-sm font-medium text-primary-content">
                    {user.pilot.nickname}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Menu items */}
          <div className="py-1">
            {/* Profile */}
            <button
              onClick={handleProfileClick}
              className="w-full px-4 py-2 text-left text-sm 
                text-primary-content hover:bg-hover transition-colors
                flex items-center gap-2"
              role="menuitem"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 
                    7h14a7 7 0 00-7-7z"
                />
              </svg>
              {t('profile.title', 'Profile')}
            </button>

            {/* Change Password */}
            <button
              onClick={handleChangePasswordClick}
              className="w-full px-4 py-2 text-left text-sm 
                text-primary-content hover:bg-hover transition-colors
                flex items-center gap-2"
              role="menuitem"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 
                    17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 
                    6 0 1121 9z"
                />
              </svg>
              {t('common.changePassword', 'Change Password')}
            </button>

            {/* Divider */}
            <div className="my-1 border-t border-divider" />

            {/* Logout */}
            <button
              onClick={handleLogout}
              className="w-full px-4 py-2 text-left text-sm 
                text-negative hover:bg-hover transition-colors
                flex items-center gap-2"
              role="menuitem"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 
                    3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                />
              </svg>
              {t('common.logout', 'Logout')}
            </button>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      <ChangePasswordModal
        isOpen={isChangePasswordOpen}
        onClose={() => setIsChangePasswordOpen(false)}
      />
    </div>
  );
}
